import get from 'lodash/get';
import { SubCompositionLayer, VideoDescriptorLayer, makeLayer } from '../layers';
import {
  CustomEventTarget,
  matchObservableObjectChange,
  findIndexFrom,
  getObservableObjectKeyPath,
  ObservableObjectChange,
  getRelativeKeyPath,
} from '@libs/util-ts';

import type { VideoDescriptor } from '../';
import { latest as LatestVDETypes } from '@libs/waymark-video/video-descriptor-types';

export class Composition extends CustomEventTarget<{
  layerAdded: VideoDescriptorLayer;
  layerRemoved: VideoDescriptorLayer;
}> {
  static ROOT_COMPOSITION_ID = '__wm__ROOT__';

  readonly id: string;

  // ProjectManifest for root composition, PreCompSource for sub-compositions.
  // Both types have compId and layers properties.
  rawCompositionData: LatestVDETypes.ProjectManifest | LatestVDETypes.PreCompSource;

  // Tracks whether this composition is detached from the video descriptor.
  isDetached = false;

  layers: VideoDescriptorLayer[];

  // Array of layers which reference this composition
  compositionInstanceLayers: Set<SubCompositionLayer>;

  videoDescriptor: VideoDescriptor;

  onVideoDescriptorChange: (evt: CustomEvent<ObservableObjectChange>) => void;

  constructor(
    compositionData: LatestVDETypes.ProjectManifest | LatestVDETypes.PreCompSource,
    videoDescriptor: VideoDescriptor,
  ) {
    super();

    if ('w' in compositionData && 'h' in compositionData) {
      // Project manifest; root composition
      this.id = Composition.ROOT_COMPOSITION_ID;
    } else {
      // PreCompSource; sub-composition
      this.id = compositionData.id;
    }

    this.rawCompositionData = compositionData;
    this.videoDescriptor = videoDescriptor;

    this.layers = [];
    this.compositionInstanceLayers = new Set();

    for (const rawLayerData of this.rawCompositionData.layers) {
      this.layers.push(makeLayer(rawLayerData, this));
    }

    // Forward layerAdded and layerRemoved events from the composition to the video descriptor
    this.addEventListener('layerAdded', (event) =>
      videoDescriptor.dispatchEvent('layerAdded', event.detail),
    );
    this.addEventListener('layerRemoved', (event) =>
      videoDescriptor.dispatchEvent('layerRemoved', event.detail),
    );

    this.onVideoDescriptorChange = (event) => {
      if (this.isDetached) {
        return;
      }

      const isDataValid = this.revalidateRawData();
      if (!isDataValid) {
        return;
      }

      const layersArrayKeyPath = getObservableObjectKeyPath(this.rawCompositionData.layers);
      if (!layersArrayKeyPath) {
        return;
      }

      const matchingLayerChange = matchObservableObjectChange(event.detail, layersArrayKeyPath);
      if (matchingLayerChange) {
        const [changedKeyPath, prevValue, newValue] = matchingLayerChange;
        // Convert the changed key path to be relative to the root of the layer's data object
        // for more convenient matching.
        const relativeKeyPath = getRelativeKeyPath(changedKeyPath, layersArrayKeyPath);
        if (relativeKeyPath !== null) {
          this.onLayersArrayChanged([relativeKeyPath, prevValue, newValue]);
        }
      }
    };
    this.videoDescriptor.addEventListener('change', this.onVideoDescriptorChange);
  }

  /**
   * Checks that this.rawCompositionData is still pointing to the latest correct object in the video descriptor.
   * Disconnections can occur if the video descriptor is mass updated via something like an autofill.
   *
   * Returns true if the raw data is valid or has been reconnected, false if it has been disconnected and should be cleaned up/ignored.
   */
  revalidateRawData() {
    if (this.isDetached) {
      // This composition has already been cleaned up, so we can ignore it
      return false;
    }

    const compositionDataKeyPath = getObservableObjectKeyPath(this.rawCompositionData);
    if (!compositionDataKeyPath) {
      return false;
    }

    let foundCompositionData = get(this.videoDescriptor.rawData, compositionDataKeyPath);

    if (this.rawCompositionData === foundCompositionData) {
      // If the currently stored rawCompositionData is the same as the video descriptor's data,
      // the raw data's reference is still valid and we can proceed.
      return true;
    }

    let foundCompID: string | null = null;

    if (foundCompositionData && Array.isArray(foundCompositionData.layers)) {
      if ('w' in foundCompositionData && 'h' in foundCompositionData) {
        // Project manifest; root composition
        foundCompID = Composition.ROOT_COMPOSITION_ID;
      } else {
        foundCompID = foundCompositionData.id;
      }
    }

    if (foundCompID === this.id) {
      // If the found composition data matches the current composition's compID,
      // we can update the rawCompositionData reference to point to it.
      this.rawCompositionData = foundCompositionData as LatestVDETypes.PreCompSource;
      return true;
    }

    // The composition data has potentially been moved somewhere else on us.
    // Let's try to find it again in the project manifest assets.

    foundCompositionData = this.videoDescriptor.rawData.projectManifest.assets.find(
      (asset) => 'id' in asset && asset.id === this.id,
    );
    if (foundCompositionData) {
      this.rawCompositionData = foundCompositionData as LatestVDETypes.PreCompSource;
      return true;
    }

    // We couldn't find the composition data in the video descriptor's project manifest assets,
    // which means it has been removed or is no longer valid. Let's clean it up.
    this.cleanUp();
    return false;
  }

  onLayersArrayChanged(change: ObservableObjectChange) {
    const [changedKeyPath] = change;

    const layersArrayKeyPath = getObservableObjectKeyPath(this.rawCompositionData.layers);
    if (!layersArrayKeyPath) {
      return;
    }

    // If the full array was replaced or the length was changed
    const didWholeArrayChange = changedKeyPath === '' || changedKeyPath === 'length';

    // Get the index if an individual item in the layers array was changed
    let startChangeIndex = Number(changedKeyPath);
    const didSingleItemChange = !Number.isNaN(startChangeIndex);

    if (!didWholeArrayChange && !didSingleItemChange) {
      return;
    }

    if (!didSingleItemChange) {
      startChangeIndex = 0;
    }

    const addedLayers = new Array<VideoDescriptorLayer>();

    // Loop over all raw layers and make any necessary updates to the cached layer instances.
    // Things that need to be done:
    //  - Emit an event for any layers that were removed and perform cleanup
    //  - Emit an event for any layers that were added
    //  - If a layer was added or removed in a way which caused an index shift, we need to update all
    //    cached layer instances to point to the correct raw layer data with that new index path
    for (let i = startChangeIndex; i < this.rawCompositionData.layers.length; i += 1) {
      const rawLayerData = this.rawCompositionData.layers[i];
      if (!rawLayerData) {
        // We may be handling a change event in the middle of a splice operation where
        // some removed items from the rawLayers array are now undefined but the length
        // of the array has not yet been updated. So, just skip over this entry if it's undefined.
        continue;
      }

      // Search for the layer instance matching this raw data entry.
      // We know that all previous layers are sorted to match the indices of their respective raw data entries,
      // so we can start searching from the current index.
      const layerInstanceIndex = findIndexFrom(
        this.layers,
        (layer) =>
          layer.getUUID() === rawLayerData.meta.uuid && layer.rawLayerData.ty === rawLayerData.ty,
        i,
      );

      if (layerInstanceIndex >= 0) {
        const layerInstance = this.layers[layerInstanceIndex];
        // Ensure the layer instance is pointing to the correct raw layer data.
        // This is important to make sure the observable object key path is correct
        // in case the layer array index shifted.
        layerInstance.rawLayerData = rawLayerData;
        if (i !== layerInstanceIndex) {
          // If we found a layer instance that matches the raw layer data at a different index,
          // swap it into the raw layer data's index.
          // By swapping, we can know that by the time we get to the end, any excess layers that need to be removed will be shifted to
          // the end of the `layers` array and can be pruned off from there.
          this.layers[layerInstanceIndex] = this.layers[i];
          this.layers[i] = layerInstance;
        }
      } else {
        // If we didn't find a layer instance that matches the raw layer data, we need to create a new one
        const newLayer = makeLayer(rawLayerData, this);
        // Insert the new layer at the correct index
        this.layers.splice(i, 0, newLayer);
        addedLayers.push(newLayer);
      }
    }

    // Since we swapped layer instances to match their raw layer data indices, any layer instances
    // that fall beyond the length of the raw layers array are excess removed layers which we can clean up.
    const excessLayers = this.layers.splice(this.rawCompositionData.layers.length);
    for (const excessLayer of excessLayers) {
      this.dispatchEvent('layerRemoved', excessLayer);
      excessLayer.cleanUp();
    }

    // Make sure we wait to dispatch the layerAdded events until after we've dispatched the layerRemoved events first
    for (const layer of addedLayers) {
      this.videoDescriptor.dispatchEvent('layerAdded', layer);
    }
  }

  getIsRootComposition() {
    return this.id === this.videoDescriptor.rootCompID;
  }

  addLayer(newLayer: VideoDescriptorLayer, suppressEvents = false) {
    if (this.layers.includes(newLayer)) {
      // The layer has already been added!
      return;
    }

    newLayer.parentComposition = this;

    this.layers.push(newLayer);
    this.rawCompositionData.layers.push(newLayer.rawLayerData);

    if (!suppressEvents) {
      this.dispatchEvent('layerAdded', newLayer);
    }
  }

  /**
   * Replaces a given layer with a new one, or removes it if a new replacement layer is not provided.
   */
  replaceLayer(oldLayer: VideoDescriptorLayer, newLayer?: VideoDescriptorLayer | null) {
    if (oldLayer.parentComposition !== this) {
      console.error(`${this.toString()}: Cannot replace layer in composition it doesn't belong to`);
      return;
    }

    const manifestLayerIndex = this.rawCompositionData.layers.indexOf(oldLayer.rawLayerData);
    if (manifestLayerIndex === -1) {
      console.error(`${this.toString()}: Unable to find layer to replace in parent composition`);
      return;
    }

    if (newLayer) {
      this.rawCompositionData.layers[manifestLayerIndex] = newLayer.rawLayerData;
    } else {
      this.rawCompositionData.layers.splice(manifestLayerIndex, 1);
    }
  }

  removeLayer(layerToRemove: VideoDescriptorLayer) {
    this.replaceLayer(layerToRemove, null);
  }

  registerCompositionInstanceLayer(layer: SubCompositionLayer) {
    if (this.compositionInstanceLayers.has(layer)) {
      // The layer has already been registered
      return;
    }

    this.compositionInstanceLayers.add(layer);
    // Listen for layerRemoved events on the layer's parent composition so we can clean it up
    // from the list of composition instance layers if it's removed
    layer.parentComposition.addEventListener('layerRemoved', (event) => {
      if (event.detail !== layer) {
        return;
      }

      this.compositionInstanceLayers.delete(layer);
      if (this.compositionInstanceLayers.size === 0) {
        // If this composition is no longer referenced by any active layers,
        // clean it up from the video descriptor to avoid memory leaks
        this.cleanUp();
      }
    });
  }

  toString() {
    return `Composition<${this.id}>`;
  }

  cleanUp() {
    this.isDetached = true;
    this.videoDescriptor.removeEventListener('change', this.onVideoDescriptorChange);
    // Make sure to run cleanup on all layers
    const layersToRemove = [...this.layers];
    for (const layer of layersToRemove) {
      layer.cleanUp();
    }
    this.videoDescriptor.removeComposition(this);
  }
}
